import {getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import {getApp} from './firebase';

const EMULATOR_PORT = null; // (usually 5000 or 5001) TODO Look in process.env.API_EMULATOR_PORT

const functions = getApp().then(app => {
  let functions = getFunctions(app);
  if (EMULATOR_PORT) {
    connectFunctionsEmulator(functions, 'localhost', EMULATOR_PORT);
  }
  return functions;
});

export async function callApi(name, data, namespace) {
  namespace = namespace || 'api';
  const auth = getAuth(await getApp());
  const user = auth.currentUser;

  try {
    const response = await httpsCallable(await functions, `${namespace}-${name}`)(data);
    return response.data;
  } catch (error) {
    if (error.code === 'functions/unauthenticated') {
      await clearRejectedSession(auth, user);
    }

    throw error;
  }
}

/** Clear only the rejected account; a delayed failure must not sign out a newer login. */
async function clearRejectedSession(auth, user) {
  if (user && auth.currentUser === user) {
    await signOut(auth);
  }
}

async function getApiBaseUrl() {
  if (process.env.VUE_APP_API_BASE_URL) {
    return process.env.VUE_APP_API_BASE_URL;
  }
  // In production, Firebase Hosting rewrites /api/** to the app function
  if (window.location.hostname !== 'localhost') {
    return '/api';
  }
  // Local dev without emulator: call the deployed function directly
  const app = await getApp();
  const projectId = app.options.projectId;
  return `https://us-central1-${projectId}.cloudfunctions.net/app`;
}

export async function requestApi(path, {
  method = 'GET',
  body,
  requireAuth = false,
  signal
} = {}) {
  const app = await getApp();
  const auth = getAuth(app);

  if (requireAuth) {
    await auth.authStateReady();

    if (!auth.currentUser) {
      throw new Error('Sign in to download this file.');
    }
  }

  const user = auth.currentUser;
  const token = await user?.getIdToken();

  if (requireAuth && (!token || auth.currentUser !== user)) {
    throw new Error('Your session changed. Please download the file again.');
  }
  const base = await getApiBaseUrl();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const request = {
    method,
    headers
  };
  if (body !== undefined) {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
    request.body = isFormData ? body : JSON.stringify(body);
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const res = await fetch(`${base}${path}`, {
    ...request,
    signal,
    ...(requireAuth ? { cache: 'no-store' } : {})
  });

  const contentType = res.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await res.json()
    : await res.text();

  if (requireAuth && auth.currentUser !== user) {
    throw new Error('Your session changed. Please download the file again.');
  }

  if (!res.ok) {
    if (res.status === 401 && token) {
      await clearRejectedSession(auth, user);
    }

    const message = payload?.error || `API error: ${res.status}`;
    const err = new Error(message);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }

  return payload;
}

export async function fetchApi(path) {
  return requestApi(path, { method: 'GET' });
}

export async function postApi(path, body) {
  return requestApi(path, { method: 'POST', body });
}

/** Request temporary file access with restored authentication and a bounded request lifetime. */
export async function fetchPrivateFileAccess(path, { signal } = {}) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  const timer = setTimeout(abort, 30000);

  signal?.addEventListener('abort', abort, { once: true });

  if (signal?.aborted) {
    abort();
  }

  try {
    return await requestApi(path, {
      requireAuth: true,
      signal: controller.signal
    });
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

/** Invalidate temporary private images when the Firebase account changes. */
export async function watchApiSession(onChange) {
  const auth = getAuth(await getApp());
  let previous = auth.currentUser;

  return onAuthStateChanged(auth, user => {
    if (user !== previous) {
      previous = user;
      onChange();
    }
  });
}
